import { unwrap } from "@nexxt/api-client";
import { reportError } from "@nexxt/app-kit";
import { type DropdownMenu, toast } from "@nexxt/ui";
import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Mail, MailX } from "lucide-react";
import type { ComponentProps } from "react";
import { m } from "../../../paraglide/messages.js";
import { api } from "../../api";
import { useCan } from "../auth/me";

// The weekly digest of the person in the menu of the user (ADR-316 §8): on Monday at 08:00, what the signals of their
// territories said and the accounts to call first. One item that says what it does, off or on again; only for who
// reads the accounts, since the digest names them. crm checks again.

type MenuAction = ComponentProps<typeof DropdownMenu>["items"][number];

const digestQuery = queryOptions({
  queryKey: ["crm", "me", "digest"],
  queryFn: () => unwrap(api.crm.me.digest.$get()),
  staleTime: 5 * 60_000,
});

/** The item of the menu, or null while the choice is not read or for who does not read the accounts. */
export function useDigestItem(): MenuAction | null {
  const readsAccounts = useCan("crm.account.read");
  const qc = useQueryClient();
  const digest = useQuery({ ...digestQuery, enabled: readsAccounts });
  const choose = useMutation({
    mutationFn: (weeklyDigest: boolean) => unwrap(api.crm.me.digest.$put({ json: { weeklyDigest } })),
    onSuccess: (choice) => {
      qc.setQueryData(digestQuery.queryKey, choice);
      toast.success(choice.weeklyDigest ? m.digest_turned_on() : m.digest_turned_off());
    },
    onError: (error) => reportError(error, m.digest_failed),
  });
  if (!readsAccounts || !digest.data) return null;
  const on = digest.data.weeklyDigest;
  return {
    id: "weekly-digest",
    label: on ? m.digest_turn_off() : m.digest_turn_on(),
    icon: on ? <MailX aria-hidden /> : <Mail aria-hidden />,
    disabled: choose.isPending,
    onSelect: () => choose.mutate(!on),
  };
}
